/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect } from 'react';

import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { MAIN_THEME } from '@styles';

import { useGeoLocationsLovs } from '../hooks';
import { FormValues } from '../types';

import { FieldArray, useFormikContext } from 'formik';
import { makeStyles } from 'tss-react/mui';

type Props = {
  status?: string;
};

type RiskRow = {
  riskDescription: string;
  geographicalLocation: string;
  order?: number;
};

const useStyles = makeStyles()(() => ({
  card: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: 4,
    padding: 16,
    marginBottom: 12,
    position: 'relative'
  },
  removeBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    position: 'absolute',
    top: 6,
    right: 6
  },
  addBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'block',
    margin: '8px auto 0'
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '0.5fr 3fr',
    gap: 10,
    marginBottom: 8
  },
  fullWidth: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 10,
    marginTop: 8
  }
}));

const RiskDescriptionWidgetAtom: React.FC<Props> = ({ status }) => {
  const { classes } = useStyles();

  const locked =
    String(status ?? '').toUpperCase() === 'CONFIRMED' ||
    String(status ?? '').toUpperCase() === 'CANCELLED';

  const { values, setFieldValue, handleBlur, touched, errors } =
    useFormikContext<FormValues>();
  const geoLocationsLovs = useGeoLocationsLovs();
  const rows = values.riskDescriptionDetails;

  const getNextOrder = () => {
    if (!rows || rows.length === 0) return 1;
    const maxOrder = Math.max(...rows.map((r) => r.order || 0));

    return maxOrder + 1;
  };

  useEffect(() => {
    if (rows && rows.length > 0) {
      let needsUpdate = false;
      const updatedRows = rows.map((row, idx) => {
        if (row.order == null || row.order === 0) {
          needsUpdate = true;

          return { ...row, order: idx + 1 };
        }

        return row;
      });

      if (needsUpdate) {
        setFieldValue('riskDescriptionDetails', updatedRows, false);
      }
    }
  }, []);

  const handleAddRow = (push: (v: RiskRow) => void) => {
    const newOrder = getNextOrder();
    push({
      riskDescription: '',
      geographicalLocation: '',
      order: newOrder
    } as RiskRow);
  };

  const handleRemoveRow = (idx: number) => {
    const currentRows = [...values.riskDescriptionDetails];
    currentRows.splice(idx, 1);

    const reordered = currentRows.map((row, newIdx) => ({
      ...row,
      order: newIdx + 1
    }));

    setFieldValue('riskDescriptionDetails', reordered, false);
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Risk Description" useSeparator={false}>
        <FieldArray
          name={'riskDescriptionDetails'}
          render={(helpers) => (
            <>
              {(rows.length
                ? rows
                : [{ riskDescription: '', geoLocationID: '', order: 1 }]
              ).map((row, i) => (
                <div key={`risk-row-${i}`} className={classes.card}>
                  <div className={classes.fullWidth}>
                    {/* <NumberInputFormField
                      name={`riskDescriptionDetails[${i}].order`}
                      title="Order*"
                      placeholder="Order"
                      hidden
                      value={row.order || i + 1}
                      error={
                        touched?.riskDescriptionDetails?.[i]?.order
                          ? (errors?.riskDescriptionDetails?.[i] as any)?.order
                          : ''
                      }
                      onBlur={(e) => handleBlur(e)}
                      onChange={(e: any) =>
                        setFieldValue(
                          `riskDescriptionDetails[${i}].order`,
                          e.target.value,
                          false
                        )
                      }
                      disabled={true}
                      material
                    /> */}

                    <TextInputFormField
                      name={`riskDescriptionDetails[${i}].riskDescription`}
                      title="Risk Description*"
                      placeholder="Risk Description"
                      value={row.riskDescription}
                      error={
                        touched?.riskDescriptionDetails?.[i]?.riskDescription
                          ? (errors?.riskDescriptionDetails?.[i] as any)
                              ?.riskDescription
                          : ''
                      }
                      onBlur={(e) => handleBlur(e)}
                      onChange={(e: any) =>
                        setFieldValue(
                          `riskDescriptionDetails[${i}].riskDescription`,
                          e.target.value,
                          false
                        )
                      }
                      disabled={locked}
                      material
                    />
                  </div>

                  <div className={classes.fullWidth}>
                    <SelectFormField
                      name={`riskDescriptionDetails[${i}].geoLocationID`}
                      title="Geographical Location*"
                      placeholder="Geographical Location"
                      value={row.geoLocationID}
                      error={
                        touched?.riskDescriptionDetails?.[i]?.geoLocationID
                          ? (errors?.riskDescriptionDetails?.[i] as any)
                              ?.geoLocationID
                          : ''
                      }
                      onBlur={(_, e) => handleBlur(e)}
                      onChange={(v: any) =>
                        setFieldValue(
                          `riskDescriptionDetails[${i}].geoLocationID`,
                          v,
                          false
                        )
                      }
                      selectOptions={geoLocationsLovs}
                      disabled={locked}
                      material
                    />
                  </div>

                  {!locked && (rows?.length ?? 0) > 1 && (
                    <button
                      className={classes.removeBtn}
                      type="button"
                      onClick={() => handleRemoveRow(i)}
                      title="Remove"
                    >
                      <ClearIcon fontSize="small" />
                    </button>
                  )}
                </div>
              ))}

              {!locked && (
                <button
                  type="button"
                  className={classes.addBtn}
                  onClick={() => handleAddRow(helpers.push)}
                  title="Add"
                >
                  <AddCircleOutlineIcon fontSize="small" color="error" />
                </button>
              )}
            </>
          )}
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export { RiskDescriptionWidgetAtom };
